import { Fragment, useId, useState } from "react";
import {
  BellOff,
  Lock,
  LogOut,
  MoreHorizontal,
  Search,
  SquarePen,
  Users,
} from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { Button, buttonVariants } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Separator } from "@/shared/components/ui/separator";
import { cn } from "@/shared/lib/utils";
import {
  useAssistants,
  useContacts,
  useOpenConversation,
} from "../api/use-directory";
import { assistantName } from "../lib/assistant-name";
import { allows } from "../lib/capabilities";
import { lockedAssistant, lockedMessage } from "../lib/handler-lock";
import type { ChatSummary, Identity, Session } from "../types";
import { listTime } from "../lib/messages";
import { GroupDialog } from "./group-dialog";
import { ContactsSheet } from "./contacts-sheet";
import { WidgetFrame } from "./widget-frame";
import { Avatar } from "./avatar";
import { ChatStatus } from "./chat-status";
import { useChatStore } from "@/store/chat";
import {
  ERROR_CLASS,
  HEADER_ICON_CLASS,
  HOVER_CARD,
  HOVER_MUTED_TEXT,
} from "./styles";

const TOOL_ITEM_CLASS =
  "min-h-11 w-full cursor-pointer justify-start px-2 text-xs font-normal motion-reduce:transition-none";

const NOTE_CLASS =
  "p-3 text-center text-xs leading-[1.7] text-muted-foreground";

const PAGE_BUTTON_CLASS =
  "mx-auto my-2 flex min-h-11 cursor-pointer px-3 text-xs text-gold-soft motion-reduce:transition-none";

export function ConversationDirectory({
  identity,
  session,
  chats,
  loading,
  error,
  onRetry,
  onSelect,
  onLogout,
  loggingOut,
  totalUnread,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  connection,
  hasEarlierPages = false,
  onResetPages,
}: {
  identity: Identity;
  session: Session;
  chats: ChatSummary[];
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
  onSelect: (chatId: string) => void;
  onLogout: () => void;
  loggingOut: boolean;
  totalUnread?: number;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  connection?: string;
  hasEarlierPages?: boolean;
  onResetPages?: () => void;
}) {
  const widgetOpen = useChatStore((state) => state.open);
  const [filter, setFilter] = useState("");
  const [contactsOpen, setContactsOpen] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const searchId = useId();
  const contacts = useContacts(identity);
  const assistants = useAssistants(identity).data?.assistants;
  const locked = lockedAssistant(assistants, session.assistant.memberId);
  const name = assistantName(undefined, assistants, session);
  const lockedText = locked ? lockedMessage(locked, name) : "";
  const chatKinds = [
    ...(allows(session, "contacts") ? (["DIRECT"] as const) : []),
    ...(allows(session, "groups") ? (["GROUP"] as const) : []),
  ];
  const showTools =
    allows(session, "contacts") || session.mode !== "glow-session";
  const orderedChats = chats
    .filter((chat) =>
      chat.title
        .toLocaleLowerCase()
        .includes(filter.trim().toLocaleLowerCase()),
    )
    .sort(
      (a, b) =>
        Number(b.peerId === session.assistant.memberId) -
        Number(a.peerId === session.assistant.memberId),
    );
  const openConversation = useOpenConversation(identity);
  const open = (peerId: string) =>
    openConversation.mutate(peerId, {
      onSuccess: ({ chatId }) => onSelect(chatId),
    });
  return (
    <WidgetFrame
      identityName={identity.name}
      demo={session.mode === "local-demo" || session.mode === "ui-mock"}
      title="Chats"
      unread={
        totalUnread ??
        chats.reduce((count, chat) => count + chat.unreadCount, 0)
      }
      headerActions={
        <>
          {chatKinds.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              aria-label="New chat"
              title="New chat"
              className={HEADER_ICON_CLASS}
              onClick={() => setCreatingGroup(true)}
            >
              <SquarePen />
            </Button>
          )}
          {showTools && (
            <details
              className="relative shrink-0"
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  event.stopPropagation();
                  event.currentTarget.open = false;
                  event.currentTarget.querySelector("summary")?.focus();
                }
              }}
            >
              <summary
                aria-label="Chat tools"
                title="Chat tools"
                className={cn(
                  buttonVariants({ variant: "ghost", size: "icon" }),
                  HEADER_ICON_CLASS,
                  "list-none [&::-webkit-details-marker]:hidden",
                )}
              >
                <MoreHorizontal />
              </summary>
              <div className="absolute top-12 right-0 z-[8] grid w-[225px] rounded-xl border border-border bg-popover p-1.5 text-popover-foreground shadow-[0_10px_25px_rgb(0_0_0/45%)]">
                {allows(session, "contacts") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Contacts"
                    className={TOOL_ITEM_CLASS}
                    onClick={() => setContactsOpen(true)}
                  >
                    <Users />
                    <span>Contacts</span>
                  </Button>
                )}
                {session.mode !== "glow-session" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Sign out of local chat"
                    className={TOOL_ITEM_CLASS}
                    disabled={loggingOut}
                    onClick={onLogout}
                  >
                    <LogOut />
                    <span>Sign out</span>
                  </Button>
                )}
              </div>
            </details>
          )}
        </>
      }
    >
      {(chats.length > 8 || hasMore || filter) && (
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
          <Search
            className="size-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <Label htmlFor={searchId} className="sr-only">
            Search loaded chats
          </Label>
          <Input
            id={searchId}
            placeholder="Search loaded chats…"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            className="h-8 min-w-0 border-transparent bg-transparent px-1 shadow-none dark:bg-transparent"
          />
        </div>
      )}
      <div className="flex min-h-0 flex-1">
        <div
          className="min-h-0 flex-1 overflow-y-auto"
          aria-label="Conversations"
        >
          {hasEarlierPages && onResetPages && (
            <Button
              variant="ghost"
              size="sm"
              className={PAGE_BUTTON_CLASS}
              onClick={onResetPages}
            >
              Back to recent chats
            </Button>
          )}
          {loading && <p className={NOTE_CLASS}>Loading conversations…</p>}
          {!loading && !chats.length && (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-muted-foreground">No chats yet.</p>
              <Button
                className="mt-4 min-h-11 w-full cursor-pointer motion-reduce:transition-none"
                onClick={() => open(session.assistant.memberId)}
                disabled={openConversation.isPending || Boolean(locked)}
              >
                {locked && <Lock aria-hidden />}
                Message {name}
              </Button>
              {locked && (
                <p
                  className="mt-2 flex min-w-0 items-center justify-center gap-1.5 text-xs text-muted-foreground"
                  title={lockedText}
                >
                  <Lock className="size-3 shrink-0" aria-hidden />
                  <span className="truncate">{lockedText}</span>
                </p>
              )}
            </div>
          )}
          {filter && !orderedChats.length && (
            <p className={NOTE_CLASS}>
              No loaded chats match.{" "}
              {hasMore ? "Load more chats to keep looking." : ""}
            </p>
          )}
          {orderedChats.map((chat) => {
            const companion =
              chat.kind === "P" &&
              chat.participants.some((person) => person.role === "ASSISTANT");
            const unread = chat.unreadCount > 0;
            const last = chat.lastMessage;
            const prefix = !last
              ? ""
              : last.memberId === identity.memberId
                ? "You: "
                : chat.kind === "G"
                  ? `${last.name}: `
                  : "";
            return (
              <Fragment key={chat.chatId}>
                <Button
                  variant="ghost"
                  className={cn(
                    cn(
                      "h-auto w-full cursor-pointer justify-start gap-3 rounded-none px-4 py-3 text-left font-normal whitespace-normal",
                      HOVER_CARD,
                    ),
                    companion && "bg-accent/4",
                  )}
                  aria-label={`Open ${chat.title}${chat.unreadCount ? `, ${chat.unreadCount} unread messages` : ""}`}
                  onClick={() => onSelect(chat.chatId)}
                >
                  <Avatar
                    name={chat.title}
                    ai={companion}
                    size="lg"
                    className={companion ? "ring-2" : undefined}
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="flex items-baseline gap-2">
                      <strong
                        className={cn(
                          "block min-w-0 flex-1 truncate text-base",
                          unread ? "font-semibold" : "font-medium",
                        )}
                      >
                        {chat.title}
                        {companion && (
                          <span className="ml-2 font-mono text-[11px] tracking-[0.05em] text-gold uppercase">
                            {name}
                          </span>
                        )}
                      </strong>
                      {last && (
                        <time
                          dateTime={last.createdAt}
                          className={cn(
                            "shrink-0 text-xs",
                            unread ? "text-gold" : "text-muted-foreground",
                          )}
                        >
                          {listTime({
                            datetime: last.createdAt,
                            utcOffset: last.utcOffset,
                          })}
                        </time>
                      )}
                    </span>
                    <span className="flex items-center gap-2">
                      <small
                        className={cn(
                          "block min-w-0 flex-1 truncate text-sm",
                          unread
                            ? "text-foreground/90"
                            : "text-muted-foreground",
                        )}
                      >
                        {prefix}
                        {last
                          ? last.text ||
                            (last.hasMedia ? "Shared media" : "Message")
                          : companion
                            ? `Talk to ${chat.title}`
                            : chat.kind === "G"
                              ? `${chat.participants.length} participants`
                              : "Private conversation"}
                      </small>
                      {chat.muted && (
                        <BellOff
                          className="size-[13px] shrink-0"
                          aria-label="Muted"
                        />
                      )}
                      {unread && (
                        <Badge
                          variant={chat.muted ? "secondary" : "default"}
                          className="h-5 min-w-5 px-1.5 font-semibold"
                        >
                          {chat.unreadCount > 99 ? "99+" : chat.unreadCount}
                        </Badge>
                      )}
                    </span>
                  </span>
                </Button>
                <Separator className="bg-border/60" />
              </Fragment>
            );
          })}
          {error && (
            <p className={ERROR_CLASS} role="alert">
              {error.message}
              <Button
                variant="ghost"
                size="sm"
                className="min-h-11 cursor-pointer motion-reduce:transition-none"
                onClick={onRetry}
              >
                Retry
              </Button>
            </p>
          )}
          {openConversation.error && (
            <p className={ERROR_CLASS} role="alert">
              {openConversation.error.message}
            </p>
          )}
          {hasMore && onLoadMore && (
            <Button
              variant="ghost"
              size="sm"
              className={PAGE_BUTTON_CLASS}
              disabled={loadingMore}
              onClick={onLoadMore}
            >
              {loadingMore ? "Loading chats…" : "Load more chats"}
            </Button>
          )}
          {chatKinds.length > 0 && (
            <div className="p-4">
              <Button
                variant="ghost"
                className={cn(
                  "min-h-11 w-full cursor-pointer border border-dashed border-border",
                  HOVER_MUTED_TEXT,
                )}
                onClick={() => setCreatingGroup(true)}
              >
                <SquarePen />
                New chat
              </Button>
            </div>
          )}
        </div>
      </div>
      {connection && <ChatStatus connection={connection} open={widgetOpen} />}
      {contactsOpen && (
        <ContactsSheet
          identity={identity}
          session={session}
          onClose={() => setContactsOpen(false)}
          onSelect={onSelect}
        />
      )}
      {creatingGroup && chatKinds.length > 0 && (
        <GroupDialog
          identity={identity}
          kinds={chatKinds}
          contacts={contacts.data?.contacts ?? []}
          onClose={() => setCreatingGroup(false)}
          onCreated={onSelect}
        />
      )}
    </WidgetFrame>
  );
}
